import React, { useContext, useEffect, useState } from "react";
import {Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, Grid, IconButton, Radio, Stack, Tooltip, useTheme} from "@mui/material";
import {useTranslation} from "react-i18next";
import {FormProvider, useForm, useFormState} from "react-hook-form";
import {FormRadioGroup, FormTextField} from "../../../common/form-fields-wrappers";
import { useRecoilState, useSetRecoilState } from "recoil";
import {keylessInfoState, generalInfoState, keylessErrorState} from "../../../search-dms/state";
import { NetworkScope } from "../../../types";
import { useHasScope } from "../../../../../config/globalConfig";
import { EditRounded } from "@mui/icons-material";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import { SET_SNACKBAR } from "../../../common/utils";
import { updateKeylessInfo } from "../../../search-dms/dmsSearch.actions";
import FormTimeField from "../../../common/form-fields-wrappers/FormTimeField";
import _ from "underscore";
import { CUR_STATUS_DMS} from "../../../data";
import { snackbarTypes } from "../../../../../util/state/globalVariables";

const INFO_DEFAULTS = {
    preparationPeriod: 336 * 60,
    thoursBeforeDeparture: 4 * 60,
    vehicleStateUpdateTimeout: 5,
    keylessMaxPreauth: 1 * 60,
    tmaxAssignedVehicle: 48 * 60,
};

type KeyLessInfoProps = {
    isNew: boolean;
    shouldSave: boolean;
    setPayload: (value: any) => void;
    onIsKeylessChanged: (value: boolean) => void;
    setPayloadInvalid: (value: boolean) => void;
}

export const timeToMinutes = (time: string) => {
    //TODO add a validation: user can insert wrong value as 99:99
    const [hoursPart, minutesPart] = time.split(":");

    return parseInt(minutesPart) + (60 * parseInt(hoursPart));
}

export const minutesToTime = (minutes: number) => {
    //console.log("minutesToTime --> keylessInfo: " + JSON.stringify(keylessInfo));

    if (isNaN(minutes) || typeof minutes !== 'number') {
        return "00:00";
    }

    const minutesPart = minutes % 60;
    const hoursPart = Math.floor(minutes / 60);

    // for format: hh:mm
    const formattedHours = String(hoursPart).padStart(2, '0');
    const formattedMinutes = String(minutesPart).padStart(2, '0');

    return `${formattedHours}:${formattedMinutes}`;
}

const KeyLessInfo = (props: KeyLessInfoProps) => {
    const {t} = useTranslation(["network", "translation"]);
    const [keylessInfo, setKeylessInfo] = useRecoilState(keylessInfoState);
    const [generalInfo, setGeneralInfo] = useRecoilState(generalInfoState);

    const keyLessInfoWithDefaults = {
        id: keylessInfo?.id,
        keyLess: keylessInfo?.keyLess ? "Yes" : "No",
        preparationPeriod: minutesToTime(keylessInfo?.preparationPeriod ?? INFO_DEFAULTS.preparationPeriod),
        thoursBeforeDeparture: minutesToTime(keylessInfo?.thoursBeforeDeparture ?? INFO_DEFAULTS.thoursBeforeDeparture),
        vehicleStateUpdateTimeout: keylessInfo?.vehicleStateUpdateTimeout ?? INFO_DEFAULTS.vehicleStateUpdateTimeout,
        keylessMaxPreauth: minutesToTime(keylessInfo?.keylessMaxPreauth ?? INFO_DEFAULTS.keylessMaxPreauth),
        tmaxAssignedVehicle: minutesToTime(keylessInfo?.tmaxAssignedVehicle ?? INFO_DEFAULTS.tmaxAssignedVehicle),
        msid: keylessInfo?.msid ? keylessInfo?.msid : generalInfo?.id,
    }

    const keylessInfoForm = useForm({
        mode: 'onChange',
        defaultValues: {
            id: keyLessInfoWithDefaults.id,
            keyLess: keyLessInfoWithDefaults.keyLess,
            preparationPeriod: keyLessInfoWithDefaults.preparationPeriod,
            thoursBeforeDeparture: keyLessInfoWithDefaults.thoursBeforeDeparture,
            vehicleStateUpdateTimeout: keyLessInfoWithDefaults.vehicleStateUpdateTimeout,
            keylessMaxPreauth: keyLessInfoWithDefaults.keylessMaxPreauth,
            tmaxAssignedVehicle: keyLessInfoWithDefaults.tmaxAssignedVehicle,
            msid: keyLessInfoWithDefaults.msid,
        }
    });

    const { errors: formErrors } = useFormState(keylessInfoForm);
    const formHasErrors = !_.isEmpty(formErrors);

    const hasScope = useHasScope;
    const theme = useTheme();
    const {dispatchGlobalData} = useContext(GlobalContext);
    const [isDisabled, setIsDisabled] = useState(true);
    const [open, setOpen] = useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const handleDisableForm = (value:boolean) => {
        setIsDisabled(value);
    }

    useEffect(() => {
        console.log({ props })
        if (props.isNew) {
            handleDisableForm(false);
        } else {
            handleDisableForm(true);
        }
    }, [props.isNew])

    const [fieldsWithErrors, setFieldsWithErrors] = useState({} as { [k: string]: boolean });
    function convertFormData(data: any) {
        const defaultValues = keylessInfoForm.formState.defaultValues!;

        const fieldIsEmpty = (name: keyof typeof defaultValues): boolean => {
            return (data[name] === undefined)
                || (data[name] === null)
                || (data[name] === '')
        }

        const isPositiveInt = (v: any) => /^\d+$/.test(v?.toString());

        const fieldsWithErrorsList = Object.keys(defaultValues).filter(fieldIsEmpty as (v: string) => boolean);

        if (! isPositiveInt(data.vehicleStateUpdateTimeout)) {
            fieldsWithErrorsList.push("vehicleStateUpdateTimeout");
        }

        if (!checkTimeLogic()) {
            fieldsWithErrorsList.push("preparationPeriod");
            //fieldsWithErrorsList.push("tmaxAssignedVehicle");
        }

        if (fieldsWithErrorsList.includes("id")) {
            const idIdx = fieldsWithErrorsList.findIndex(v => v === "id");
            fieldsWithErrorsList.splice(idIdx, 1);
        }

        setFieldsWithErrors(Object.fromEntries(fieldsWithErrorsList.map((field: string) => [field, true])));

        if (isKeyLess && (formHasErrors || fieldsWithErrorsList.length)) {
            console.log({ fieldsWithErrorsList, formHasErrors })
            if (props.shouldSave) {
                props.setPayloadInvalid(true);
            }
            return null;
        }

        return {
            id: keylessInfo?.id,
            keyLess: data?.keyLess === "Yes",
            preparationPeriod: timeToMinutes(data?.preparationPeriod),
            thoursBeforeDeparture: timeToMinutes(data?.thoursBeforeDeparture),
            vehicleStateUpdateTimeout: data?.vehicleStateUpdateTimeout,
            keylessMaxPreauth: timeToMinutes(data?.keylessMaxPreauth),
            tmaxAssignedVehicle: timeToMinutes(data?.tmaxAssignedVehicle),
            msid: keylessInfo?.msid ? keylessInfo?.msid : generalInfo?.id,
        }
    }

    useEffect(() => {
        if (props.shouldSave) {
            const data = convertFormData(keylessInfoForm.getValues());
            if (!data) return; // Error was thrown
            props.setPayload(data);
        }
    }, [props.shouldSave])

    const [isKeyLess, setIsKeyLess] = useState(keylessInfo?.keyLess);
    function handleKeylessChange(_: any, option: string) {
        const value = option === "Yes";

        const keylessGotEnabled = !isKeyLess && value;

        setIsKeyLess(value)
        props.onIsKeylessChanged(value);

        if (keylessGotEnabled) {
            dispatchGlobalData({
                type: 'SET_SNACKBAR',
                payload: {
                    snackbar: {
                        message: t("dmsDetail.keyless.infoTab.fillOperationArea"),
                        type: snackbarTypes.info,
                        show: true,
                        autoClose: true,
                    }
                }
            });
        }
    }

    const fieldIsDisabled = (name: string): boolean => {
        if (name === "keyLess") {
            return isDisabled;
        }

        if (props.isNew) {
            return !isKeyLess;
        }

        if (!props.isNew) {
            return isDisabled || !isKeyLess;
        }

        return true; // Unreachable statement
    }

    const setKeylessError = useSetRecoilState(keylessErrorState);
    const onSubmit = (data: any) => {
        if (formHasErrors) {
            if (props.shouldSave) {
                props.setPayloadInvalid(true);
            }
            dispatchGlobalData(SET_SNACKBAR.ERROR);
            return;
        }

        const reqParameters = convertFormData(data);
        if (!reqParameters) return; // Error was thrown
        setOpen(false);
        if (!reqParameters) return; // Error was thrown

        updateKeylessInfo(reqParameters)
            .then( (res:any) => {
                setKeylessInfo(res?.msKeylessInfo);
                setIsDisabled(true);
                dispatchGlobalData(SET_SNACKBAR.SUCCESS);
            })
            .catch((err: any) => {
                setKeylessError(err);
            });
    }

    useEffect(() => {
        console.log("useEffect --> keylessInfo: " + JSON.stringify(keylessInfo));
        keylessInfoForm.setValue("id", keyLessInfoWithDefaults.id);
        keylessInfoForm.setValue("keyLess", keyLessInfoWithDefaults.keyLess);
        keylessInfoForm.setValue("preparationPeriod", keyLessInfoWithDefaults.preparationPeriod);
        keylessInfoForm.setValue("thoursBeforeDeparture", keyLessInfoWithDefaults.thoursBeforeDeparture);
        keylessInfoForm.setValue("vehicleStateUpdateTimeout", keyLessInfoWithDefaults.vehicleStateUpdateTimeout);
        keylessInfoForm.setValue("keylessMaxPreauth", keyLessInfoWithDefaults.keylessMaxPreauth);
        keylessInfoForm.setValue("tmaxAssignedVehicle", keyLessInfoWithDefaults.tmaxAssignedVehicle);
    },[keylessInfo])


    const checkTimeLogic =() =>{
        console.log("checkTimeLogic preparationPeriod: " + keylessInfoForm.getValues("preparationPeriod"));
        console.log("checkTimeLogic tmaxAssignedVehicle: " + keylessInfoForm.getValues("tmaxAssignedVehicle"));
        return timeToMinutes(keylessInfoForm.getValues("preparationPeriod")) >= timeToMinutes(keylessInfoForm.getValues("tmaxAssignedVehicle"));
    }

    return (
        <>
            <FormProvider { ...keylessInfoForm }>
                <Grid container item direction="row" xs={12} columnSpacing={2}>
                    <Grid item xs={12}>
                        <Stack direction="column" spacing={2} justifyContent="flex-end" alignItems="flex-end">
                            {hasScope(NetworkScope.MsMng) && isDisabled && generalInfo?.currentStatus !== CUR_STATUS_DMS.TERMINATED &&
                                <IconButton color={"accent"} onClick={() => {handleDisableForm(false)}}>
                                    <EditRounded/>
                                </IconButton>
                            }
                        </Stack>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <FormRadioGroup
                            control={keylessInfoForm.control}
                            name="keyLess"
                            label={t("dmsDetail.keyless.infoTab.keyLess")}
                            disabled={fieldIsDisabled("keyLess")}
                            onChange={handleKeylessChange}
                        >
                            <FormControlLabel
                                value="Yes"
                                control={<Radio/>}
                                label={t("dmsDetail.keyless.yes")}
                            />
                            <FormControlLabel
                                value="No"
                                control={<Radio />}
                                label={t("dmsDetail.keyless.no")}
                            />
                        </FormRadioGroup>
                    </Grid>

                    <Grid item xs={3}>
                        <Tooltip title={t('dmsDetail.keyless.infoTab.tooltips.preparationPeriod')}>
                            <FormTimeField
                                error={!!fieldsWithErrors.preparationPeriod}
                                disabled={fieldIsDisabled("preparationPeriod")}
                                control={keylessInfoForm.control}
                                name="preparationPeriod"
                                label={t("dmsDetail.keyless.infoTab.preparationPeriod")}
                                
                            />
                        </Tooltip>
                        {fieldsWithErrors.preparationPeriod && (
                            <p style={{ color: "red", fontSize: "0.875rem", marginTop: "4px" }}>
                             { t('dmsDetail.keyless.infoTab.tooltips.preparationPeriodLessThenMaxAssigned') +  t('dmsDetail.keyless.infoTab.tmaxAssignedVehicle')}
                            </p>
                            )}
                    </Grid>
                    <Grid item xs={3}>
                        <Tooltip title={t('dmsDetail.keyless.infoTab.tooltips.thoursBeforeDeparture')}>
                            <FormTimeField
                                error={!!fieldsWithErrors.thoursBeforeDeparture}
                                disabled={fieldIsDisabled("thoursBeforeDeparture")}
                                control={keylessInfoForm.control}
                                name="thoursBeforeDeparture"
                                label={t("dmsDetail.keyless.infoTab.thoursBeforeDeparture")}
                            />
                        </Tooltip>
                    </Grid>
                    <Grid item xs={3}>
                        <Tooltip title={t('dmsDetail.keyless.infoTab.tooltips.vehicleStateUpdateTimeout')}>
                            <FormTextField  
                                error={!!fieldsWithErrors.vehicleStateUpdateTimeout}
                                disabled={fieldIsDisabled("vehicleStateUpdateTimeout")}
                                control={keylessInfoForm.control}
                                name="vehicleStateUpdateTimeout"
                                label={t("dmsDetail.keyless.infoTab.vehicleStateUpdateTimeout")}
                            />
                        </Tooltip>
                    </Grid>
                    <Grid item xs={3}>
                        <Tooltip title={t('dmsDetail.keyless.infoTab.tooltips.keylessMaxPreauth')}>
                            <FormTimeField
                                error={!!fieldsWithErrors.keylessMaxPreauth}
                                disabled={fieldIsDisabled("keylessMaxPreauth")}
                                control={keylessInfoForm.control}
                                name="keylessMaxPreauth"
                                label={t("dmsDetail.keyless.infoTab.keylessMaxPreauth")}
                            />
                        </Tooltip>
                    </Grid>
                    <Grid item xs={3}>
                        <Tooltip title={t('dmsDetail.keyless.infoTab.tooltips.tmaxAssignedVehicle')}>
                            <FormTimeField
                                error={!!fieldsWithErrors.tmaxAssignedVehicle}
                                disabled={fieldIsDisabled("tmaxAssignedVehicle")}
                                control={keylessInfoForm.control}
                                name="tmaxAssignedVehicle"
                                label={t("dmsDetail.keyless.infoTab.tmaxAssignedVehicle")}
                            />
                        </Tooltip>
                    </Grid>
                </Grid>

                <Grid item xs={12} sx={{marginTop: theme.spacing(2)}}>
                    <Stack direction="column" spacing={2} justifyContent="flex-end" alignItems="flex-end">
                        { !isDisabled && !props.isNew &&
                            <Button variant="contained" onClick={handleOpen} disabled={formHasErrors} >
                                {t("dmsDetail.keyless.infoTab.button.edit")}
                            </Button>
                        }
                    </Stack>
                </Grid>
            </FormProvider>

            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {t("dmsDetail.keyless.infoTab.dialog.title")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {t("dmsDetail.keyless.infoTab.dialog.content")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}  variant={"outlined"} color={"accent"}>
                        {t("dmsDetail.keyless.infoTab.dialog.action.cancel")}
                    </Button>
                    <Button onClick={keylessInfoForm.handleSubmit(onSubmit)} autoFocus>
                        {t("dmsDetail.keyless.infoTab.dialog.action.confirm")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default KeyLessInfo
